import { Action as ReduxAction } from "redux";
import { ThunkAction as ReduxThunkAction, ThunkDispatch } from "redux-thunk";
import { IUhfViewState } from "../../pages/Contracts";
import { MetaTypes } from "./MetaTypes";
// tslint:disable-next-line:interface-name
export interface Action<T> extends ReduxAction<T> {
    /**
     * The meta property for the action (see Flux Standard Actions)
     */
    meta?: { [key: string]: any };
}

/**
 * A better typing for the Redux Action
 */
// tslint:disable-next-line:interface-name
export interface ActionWithPayload<T extends string, P> extends Action<T> {
    /**
     * The payload of this action
     */
    payload: P;
}

/**
 * @param T - Type
 * @param R - Return Type
 */
 export interface ThunkAction<T extends string, R, A extends Action<T> = Action<T>> extends ReduxThunkAction<R, IUhfViewState, unknown, A> {

}

/**
 * @param T - Type
 * @param R - Return Type
 * @param S - State (IUhfViewState)
 * @param E - Extra args (unknown)
 * @param A - ActionWithPayload
 */
 export interface ThunkActionWithPayload<T extends string, P, R> extends ThunkAction<T, R, ActionWithPayload<T, P>> {
}


export interface ThunkActionType<T extends string> extends ReduxAction<T> {
    /**
     * The meta property for the action (see Flux Standard Actions)
     */
    meta?: { [key: string]: any };
    resolve: (value?: any) => void;
    reject: (error?: any) => void;
}

export interface ThunkActionWithPayloadType<T extends string, P> extends ThunkActionType<T>  {
    payload: P;
}


/**
 * Create a new action with type and payload
 * @param type The action type
 * @param payload The payload
 */
export function createAction<T extends string>(type: T): Action<T>;
export function createAction<T extends string, P>(type: T, payload: P, meta?: { [key: string]: string }): ActionWithPayload<T, P>;
// tslint:disable-next-line:typedef
export function createAction<T extends string, P>(type: T, payload?: P, meta?: { [key: string]: string }) {
    return { type, payload, meta };
}

/**
 * Create a new thunk action with type and payload
 * @param type The action type
 * @param payload The payload
 */
 export function createThunkActionType<T extends string>(type: T): ThunkActionType<T>;
 export function createThunkActionType<T extends string, P>(type: T, payload: P, meta?: { [key: string]: string }): ThunkActionWithPayloadType<T, P>;
 // tslint:disable-next-line:typedef
 export function createThunkActionType<T extends string, P>(type: T, payload?: P, meta?: { [key: string]: string }) {
     return { type, payload, meta, resolve: () => {}, reject: () => {} };
 }

/**
 * Create a new action with type and payload
 * @param type The action type
 * @param payload The payload
 */
 export function createActionWithCallback<R>(type: string) : ThunkAction<string, Promise<R>>;
 export function createActionWithCallback<R>(type: string, payload: any, meta?: { [key: string]: string }) : ThunkActionWithPayload<string, typeof payload, Promise<R>>;
 // tslint:disable-next-line:typedef
 export function createActionWithCallback<R>(type: string, payload?: any, meta?: { [key: string]: string }): ThunkActionWithPayload<string, typeof payload, Promise<R>> {
    return (dispatch: ThunkDispatch<IUhfViewState, undefined, ActionWithPayload<string, typeof payload>>) => {
        return new Promise<R>((resolve, reject) => {
            dispatch({
                type,
                payload,
                resolve, 
                reject
            });
        });
    }
 }

/**
 * Creates an action which indicate that this is an action required for TTI
 */
export function ttiAction<T, A extends Action<T>>(action: A): A {
    if (!action.meta) {
        action.meta = {};
    }

    action.meta[MetaTypes.TTI_ACTION] = MetaTypes.TTI_ACTION;
    return action;
}

export function trackedAction<T, A extends Action<T>>(action: A, telemetryProps?: { [key: string]: any }): A {
    if (!action.meta) {
        action.meta = {};
    }

    action.meta[MetaTypes.TELEMETRY_PROPS] = telemetryProps || {};
    return action;
}

export function trackedErrorAction<T extends string, A extends ActionWithPayload<T, any>>(action: A): A {
    if (!action.meta) {
        action.meta = {};
    }

    action.meta[MetaTypes.ERROR_TELEMETRY_PROPS] = action.payload || {};
    return action;
}

/**
 * @copyright Copyright (c) 2018 Martin Hochel
 * Borrowed from the rex-tils library
 */

type ActionsCreatorsMapObject = { [actionCreator: string]: (...args: any[]) => any };
export type ActionsUnion<A extends ActionsCreatorsMapObject> = ReturnType<A[keyof A]>;
export type ActionsOfType<ActionUnion, ActionType extends string> = ActionUnion extends Action<ActionType> ? ActionUnion : never;
